import { create } from 'zustand'
import type {
  InputSpeechData,
  Preview,
  SpeechData,
} from '@/features/session/domain/models/session.ts'
import { createSTSSpeech } from '@/features/speech/domain/use-cases/create-sts-speech'
import { createTTSSpeech } from '@/features/speech/domain/use-cases/create-tts-speech'
import { getAudioBufferFromAudioUrl } from '@/utils/get-audio-buffer-from-audio-url'

export const speechSelector =
  (id: string) =>
  (state: SpeechStore): SpeechData | undefined =>
    state.speeches[id]

interface SpeechStore {
  preview?: Preview | undefined
  speeches: Record<string, SpeechData>
  findOneByScriptId: (scriptId: string) => SpeechData | undefined
  storeSpeech: (speechId: string, speechData: SpeechData) => void
  storeSpeeches: (speeches: Record<string, SpeechData>) => void
  clearPreview: () => void
  regeneratePreview: (data: InputSpeechData) => Promise<Preview>
}

export const useSpeechStore = create<SpeechStore>((set, get) => ({
  speeches: {},
  findOneByScriptId: (scriptId: string) => {
    const { speeches } = get()
    return Object.values(speeches).find(
      (speech) => speech.scriptId === scriptId
    )
  },
  storeSpeech: (speechId, speechData) => {
    set(({ speeches }) => ({
      speeches: { ...speeches, [speechId]: speechData },
    }))
  },
  storeSpeeches: (speeches: Record<string, SpeechData>) => {
    set(() => ({ speeches: { ...speeches } }))
  },
  clearPreview: () => {
    set({ preview: undefined })
  },
  regeneratePreview: async ({
    type,
    scriptText,
    voice,
    expressive,
    speed,
    fileId,
  }: InputSpeechData) => {
    if (!fileId && type === 'sts') {
      throw new Error('File ID is required for STS')
    }
    const scriptId = crypto.randomUUID()
    const newSpeech =
      type === 'sts'
        ? await createSTSSpeech(scriptId, fileId ?? '', voice, expressive)
        : await createTTSSpeech(scriptId, voice, speed, expressive, scriptText)
    const preview: Preview = {
      id: crypto.randomUUID(),
      buffer: await getAudioBufferFromAudioUrl(newSpeech.speechUrl),
    }
    set({ preview })
    return preview
  },
}))
